<template>
    <div>
        <form @submit.prevent="handleSubmit">

            <!-- Login input field -->
            <email-address-field v-model.lazy="email" class="contentsquare_sensitive_data"/>

            <!-- Password input field -->
            <password-field
                class="contentsquare_sensitive_data"
                v-model="password"
                :require-strong-password="false"/>

            <div v-if="showResetPassword">
                <h4 class="pl-3 mt-4">
                    {{ $t('PASSWORD_FIELD_HAVING_TROUBLE') }}
                    <router-link
                        :to="{ name: 'forgot-password' }"
                        class='link'
                        v-text="$t('PASSWORD_FIELD_TRY_RESET')">
                    </router-link>
                </h4>

                <!-- Small gap -->
                <div class="clear--sm"></div>
            </div>

            <!-- Submit button -->
            <div class="text-center container-padding">
                <div v-if="showIncorrectCredentialsMessage" class="input input--invalid">
                    <p class="input__message">{{ $t('PASSWORD_FIELD_WHOOPS') }}</p>
                </div>
                <submit-button class="btn btn--full-width" :processing="isFormProcessing" color="primary-black" :hoverable="false">{{ $t('LOGIN_LOG_IN')}}</submit-button>
            </div>
        </form>
        <stripe-terminal-reader v-if="isCanvasserType"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { CANVASSER_TYPE, USER_TYPE } from '@/constants/Account';
import AuthService from '@/services/auth/AuthService';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import PasswordField from '@/components/forms/fields/PasswordField';
import EmailAddressField from '@/components/forms/fields/EmailAddressField';
import StripeTerminalReader from '../cardDetails/StripeTerminalReader';
import { CSPageEvents } from '@/helpers/GAHelpers';
import { setCookie } from '@/helpers/QueryHelpers';

let authService = new AuthService();

export default {
    name: 'LoginForm',
    components: {
        SubmitButton,
        PasswordField,
        EmailAddressField,
        StripeTerminalReader,
    },
    props: {
        showResetPassword: {
            default: true,
            type: Boolean,
        },
        isCanvasser: {
            default: false,
            type: Boolean,
        },
        redirectPathAfterLogin: {
            default: null,
            type: String,
        },
    },
    data() {
        return {
            email: null,
            password: null,
            incorrectCredentials: false,
            isFormProcessing: false,
        };
    },
    computed: {
        ...mapGetters({
            intendedUrl: 'auth/getIntendedUrl',
            isUserLoggedIn: 'auth/isUserLoggedIn',
            isCanvasserType: 'auth/isCanvasser',
        }),
        showIncorrectCredentialsMessage() {
            return this.incorrectCredentials;
        },
    },
    mounted() {
        if (this.isUserLoggedIn) this.redirectToIntendedUrl();
    },
    methods: {
        async authenticateUser() {
            this.isCanvasser ? this.canvasserLogin() : this.login();
        },

        handleLoginErrorResponse() {
            this.showErrorMessage();
            this.toggleFormProcessing();
        },

        handleLoginSuccessResponse(userType, data = {}) {
            if (data.tokens) {
                localStorage.setItem('access_token', data.tokens.access_token);
                localStorage.setItem('refresh_token', data.tokens.refresh_token);
            }

            if (this.$globalQueryParams.uuid) {
                this.$globalQueryParams.uuid = null;
            }

            this.$store.dispatch('auth/loginUser', userType);

            'redirect' in data ? this.redirectToGivenPage(data) : this.redirectToIntendedUrl();
        },

        redirectToGivenPage(data) {
            this.$store.commit('account/updateRescheduledDeliveryDays', data.details);
            this.$router.push({ name: data.redirect });
        },

        handleSubmit() {
            this.toggleFormProcessing();

            setTimeout(() => {
                this.authenticateUser();
            }, 500);
        },

        canvasserLogin() {
            authService.canvasserLogin(this.email, this.password)
                .then((response) => {
                    if (response.data.tokens) {
                        document.cookie = `access_token=${response.data.tokens.access_token}; path=/;`;
                    }
                    this.handleLoginSuccessResponse(CANVASSER_TYPE);
                })
                .catch(() => this.handleLoginErrorResponse());
        },

        login() {
            authService.login(this.email, this.password)
                .then((response) => {
                    CSPageEvents(undefined, { userLogged: 'Logged-in' });
                    return this.handleLoginSuccessResponse(USER_TYPE, response.data);
                })
                .catch((error) => {
                    console.error({ error });
                    this.handleLoginErrorResponse();
                });
        },

        redirectToIntendedUrl() {
            const redirectPath = this.redirectPathAfterLogin ? this.redirectPathAfterLogin : this.intendedUrl;

            setTimeout(() => this.$router.push(redirectPath), 0);
        },

        showErrorMessage() {
            this.incorrectCredentials = true;
        },

        toggleFormProcessing() {
            this.isFormProcessing = !this.isFormProcessing;
        },
    },
};
</script>

<style scoped>
    .container-padding {
        padding: 0.75rem;
    }
</style>
